import { SITE_URL, SITE_LOGO, absoluteUrl } from '@/seo/site';
import { faqs } from '@/pages/bookkeeping/components/FAQSection';

type JsonLdNode = Record<string, unknown>;

const organizationRef: JsonLdNode = {
  '@type': 'Organization',
  name: 'Penland LLC',
  url: `${SITE_URL}/`,
};

const postalAddress: JsonLdNode = {
  '@type': 'PostalAddress',
  postOfficeBoxNumber: '432',
  addressLocality: 'Midlothian',
  addressRegion: 'VA',
  postalCode: '23113',
  addressCountry: 'US',
};

function faqNode(): JsonLdNode {
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };
}

/**
 * Returns the Schema.org JSON-LD nodes for a given route.
 * Only nodes whose facts are visible on the matching page are included.
 */
export function schemaForRoute(pathname: string): JsonLdNode[] {
  switch (pathname) {
    case '/':
      return [
        {
          '@type': 'Organization',
          name: 'Penland LLC',
          url: `${SITE_URL}/`,
          logo: SITE_LOGO,
          description:
            'Penland LLC is a Virginia company that owns and operates a small group of businesses in bookkeeping, consulting and web development, property management, software, and travel.',
          email: 'info@penlandllc.com',
          telephone: '+1-804-464-7418',
          address: postalAddress,
          sameAs: ['https://matchlineup.com', 'https://alabastertravel.com/'],
        },
        {
          '@type': 'WebSite',
          name: 'Penland LLC',
          url: `${SITE_URL}/`,
        },
      ];

    case '/bookkeeping':
      return [
        {
          '@type': 'AccountingService',
          name: 'Penland Bookkeeping',
          url: absoluteUrl('/bookkeeping'),
          description:
            'Specialized bookkeeping for restaurants, cafes, bars, skilled trades, small businesses, and nonprofits in Richmond, Henrico, and Chesterfield, Virginia.',
          email: 'info@penlandllc.com',
          telephone: '+1-804-464-7418',
          address: postalAddress,
          parentOrganization: organizationRef,
          areaServed: [
            { '@type': 'City', name: 'Richmond', address: { '@type': 'PostalAddress', addressRegion: 'VA', addressCountry: 'US' } },
            { '@type': 'AdministrativeArea', name: 'Henrico County' },
            { '@type': 'AdministrativeArea', name: 'Chesterfield County' },
          ],
        },
        faqNode(),
      ];

    case '/consulting':
      return [
        {
          '@type': 'ProfessionalService',
          name: 'Penland Consulting & Web Development',
          url: absoluteUrl('/consulting'),
          description:
            'Penland LLC provides operations, financial, and web development consulting for owner-operated businesses and nonprofits in Virginia.',
          email: 'info@penlandllc.com',
          telephone: '+1-804-464-7418',
          address: postalAddress,
          parentOrganization: organizationRef,
        },
      ];

    case '/properties':
      return [
        {
          '@type': 'Service',
          serviceType: 'Residential property management',
          name: 'Penland Properties',
          url: absoluteUrl('/properties'),
          description:
            'Penland Properties manages long-term and short-term residential rental property, handling leasing, tenant relations, maintenance, and taxes in Virginia.',
          email: 'properties@penlandllc.com',
          telephone: '+1-804-464-7418',
          provider: organizationRef,
        },
      ];

    case '/matchlineup':
      return [
        {
          '@type': 'SoftwareApplication',
          name: 'MatchLineup',
          applicationCategory: 'SportsApplication',
          operatingSystem: 'Web',
          url: 'https://matchlineup.com',
          description:
            'MatchLineup is tennis match scheduling software for adult recreational teams. Captains post matches, players respond, and lineups are set in minutes.',
          publisher: organizationRef,
        },
      ];

    case '/alabaster':
      return [
        {
          '@type': 'TravelAgency',
          name: 'Alabaster Travel',
          url: 'https://alabastertravel.com/',
          description:
            'Alabaster Travel offers independent travel advisory, itinerary design, booking, group travel, and concierge support for individuals, families, and groups.',
          email: 'hello@alabastertravel.com',
          parentOrganization: organizationRef,
        },
      ];

    default:
      return [];
  }
}